import React, { useEffect, useState, useCallback } from "react";
import { Formik, Form, FieldArray } from "formik";
import { Row, Col, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import SingleSelect from "components/common/SingleSelect";
import CustomField from "components/form/CustomField";
import PageHeader from "components/common/PageHeader";
import { useQueryParam } from "hooks/useQueryParam";
import { useSmartBack } from "hooks/useSmartBack";
import { usePermission } from "hooks/usePermission";
import { handleSubmitForm } from "helpers/apiUtils";
import { getPlansList } from "store/slices/membershipSlice";
import {
  addDynamicPrices,
  getDynamicPricesDetails,
  updateDynamicPrices,
} from "store/slices/dynamicPricesSlice";
import { COUPON_DISCOUNT_TYPE } from "utils/constants";
import { formatOptionsForSelect } from "utils/formatOptions";
import { getDynamicPricesValidationSchema } from "utils/validations";
import { Icons } from "assets/icons";

const EMPTY_RULE = { maxCustomers: "", type: "", value: "" };

const DynamicPrices = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const id = useQueryParam("id");
  const isEdit = mode === "edit";
  const goBack = useSmartBack();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { canAction } = usePermission();
  const { canAdd, canEdit } = canAction("freewash_rule");
  const { trash } = Icons || {};

  const { plansList, isLoading } = useSelector((state) => state.Membership);
  const { loading, itemDetails } = useSelector((state) => state.DynamicPrices);

  const [initialValues, setInitialValues] = useState({
    planId: "",
    rules: [EMPTY_RULE],
  });

  useEffect(() => {
    dispatch(getPlansList());
  }, [dispatch]);

  useEffect(() => {
    if (isEdit && id) {
      dispatch(getDynamicPricesDetails(id));
    }
  }, [isEdit, id, dispatch]);

  // Populate form in edit mode
  useEffect(() => {
    if (isEdit && id && itemDetails) {
      setInitialValues({
        planId: itemDetails.planId?._id || itemDetails.planId || "",
        rules: itemDetails.rules?.length
          ? itemDetails.rules.map(({ maxCustomers, type, value }) => ({
              maxCustomers: maxCustomers ?? "",
              type: type || "",
              value: value ?? "",
            }))
          : [EMPTY_RULE],
      });
    }
  }, [isEdit, id, itemDetails]);

  const handleSubmit = useCallback(
    (values) => {
      const payload = {
        planId: values.planId,
        rules: values.rules.map((rule) => ({
          maxCustomers: Number(rule.maxCustomers),
          type: rule.type,
          value: Number(rule.value),
        })),
      };

      handleSubmitForm({
        dispatch,
        action: isEdit ? updateDynamicPrices : addDynamicPrices,
        payload: isEdit ? { ...payload, id } : payload,
        onSuccess: () => navigate(-1),
      });
    },
    [dispatch, isEdit, id, navigate],
  );

  if (!canEdit && isEdit) return null;
  if (!canAdd && !isEdit) return null;

  if (isEdit && loading) {
    return (
      <div className="g_center my-5">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="page-content">
      <PageHeader
        isBackBtn
        title={
          isEdit ? t("Edit Dynamic Price Rule") : t("Add Dynamic Price Rule")
        }
      />
      <div className="common_card">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={getDynamicPricesValidationSchema(t)}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form>
              {/* ── Plan ── */}
              <Row className="mb-4">
                <Col lg={6}>
                  <div className="form_field">
                    <SingleSelect
                      label={t("Plan")}
                      required
                      value={values.planId}
                      options={formatOptionsForSelect(plansList, language)}
                      handleSingleSelectChange={(e) =>
                        setFieldValue("planId", e.value)
                      }
                      error={
                        errors.planId && touched.planId ? errors.planId : null
                      }
                    />
                  </div>
                </Col>
              </Row>

              {/* ── Rules ── */}
              {/* ── Rules ── */}
              <FieldArray name="rules">
                {({ push, remove }) => (
                  <>
                    {values.rules.map((rule, index) => {
                      const ruleErrors = errors.rules?.[index] || {};
                      const ruleTouched = touched.rules?.[index] || {};

                      return (
                        <div
                          key={index}
                          className={`dynamic_rule_group ${index !== 0 ? "with-border" : ""} mb-2`}
                        >
                          <Row>
                            <Col lg={4}>
                              <div className="form_field">
                                <SingleSelect
                                  required
                                  label={t("Type")}
                                  options={COUPON_DISCOUNT_TYPE}
                                  value={rule.type}
                                  handleSingleSelectChange={(opt) =>
                                    setFieldValue(
                                      `rules[${index}].type`,
                                      opt.value,
                                    )
                                  }
                                  error={
                                    ruleErrors.type && ruleTouched.type
                                      ? ruleErrors.type
                                      : null
                                  }
                                />
                              </div>
                            </Col>

                            <Col lg={3}>
                              <div className="form_field">
                                <CustomField
                                  label={t("Value")}
                                  name={`rules[${index}].value`}
                                  placeholder={
                                    rule.type === "percentage"
                                      ? t("10%")
                                      : t("10 SAR")
                                  }
                                  required
                                />
                              </div>
                            </Col>

                            <Col lg={3}>
                              <div className="form_field">
                                <CustomField
                                  label={t("Maximum Customers")}
                                  name={`rules[${index}].maxCustomers`}
                                  placeholder={t("Enter maximum customers")}
                                  required
                                />
                              </div>
                            </Col>
                            <Col lg={2} className="d-flex align-items-center">
                              <div className="">
                                {values.rules.length > 1 && (
                                  <img
                                    src={trash}
                                    alt="trash"
                                    width={20}
                                    height={20}
                                    className="c_p"
                                    onClick={() => remove(index)}
                                  />
                                )}
                              </div>
                            </Col>
                          </Row>
                        </div>
                      );
                    })}

                    {/* ── Add Rule + Remove Last ── */}
                    <div className="d-flex align-items-center justify-content-between gap-2 mt-2 mb-4">
                      <button
                        type="button"
                        className="main_btn outline"
                        onClick={() => push({ ...EMPTY_RULE })}
                      >
                        + {t("Add Rule")}
                      </button>
                    </div>
                  </>
                )}
              </FieldArray>

              {/* ── Actions ── */}
              <Row>
                <Col lg={12} className="d-flex justify-content-end gap-2 mt-3">
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={goBack}
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    type="submit"
                    className="main_btn"
                    disabled={isLoading}
                  >
                    {isEdit ? t("Save Changes") : t("Add Dynamic Price")}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default DynamicPrices;
